import type { ApiFailure } from "../types";

const API_ROOT = "/api/v1";

export class ApiError extends Error implements ApiFailure {
  code: string;
  correlationId?: string;
  fieldErrors?: Record<string, string>;
  status: number;
  latest?: ApiFailure["latest"];

  constructor(message: string, options: Partial<ApiFailure> & { status?: number } = {}) {
    super(message);
    this.name = "ApiError";
    this.code = options.code ?? "request_failed";
    this.correlationId = options.correlationId;
    this.fieldErrors = options.fieldErrors;
    this.status = options.status ?? 0;
    this.latest = options.latest;
  }
}

function csrfToken(): string {
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("csrftoken="))
    ?.slice("csrftoken=".length) ?? "";
}

async function ensureCsrfToken(): Promise<string> {
  const existing = csrfToken();
  if (existing) return existing;

  const response = await fetch(`${API_ROOT}/csrf`, {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new ApiError("We couldn’t establish a secure browser session. Refresh and try again.", {
      code: "csrf_unavailable",
      status: response.status,
    });
  }
  const token = csrfToken();
  if (!token) {
    throw new ApiError("We couldn’t establish a secure browser session. Refresh and try again.", {
      code: "csrf_unavailable",
    });
  }
  return token;
}

interface ErrorPayload {
  error?: {
    code?: string;
    message?: string;
    correlation_id?: string;
    fields?: Record<string, string>;
  };
  latest?: unknown;
}

type JsonRequestInit = Omit<RequestInit, "body"> & { body?: unknown };

export async function request<T>(path: string, init: JsonRequestInit = {}): Promise<T> {
  const method = init.method ?? "GET";
  const isMutation = !["GET", "HEAD"].includes(method);
  if (!navigator.onLine && isMutation) {
    throw new ApiError("You’re offline. Connect before changing or saving this plan.", { code: "offline" });
  }

  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body !== undefined) headers.set("Content-Type", "application/json");
  if (isMutation) {
    try {
      headers.set("X-CSRFToken", decodeURIComponent(await ensureCsrfToken()));
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError("We couldn’t establish a secure browser session. Refresh and try again.", {
        code: "csrf_unavailable",
      });
    }
  }

  let response: Response;
  try {
    response = await fetch(`${API_ROOT}${path}`, {
      ...init,
      method,
      headers,
      credentials: "same-origin",
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    throw new ApiError("We couldn’t connect. Check your connection and try again.", { code: "network_error" });
  }

  const payload = (await response.json().catch(() => null)) as (T & ErrorPayload) | null;
  if (!response.ok) {
    throw new ApiError(payload?.error?.message ?? "We couldn’t complete that request.", {
      code: payload?.error?.code ?? "request_failed",
      correlationId: payload?.error?.correlation_id,
      fieldErrors: payload?.error?.fields,
      status: response.status,
      latest: payload?.latest as ApiFailure["latest"],
    });
  }
  return payload as T;
}
